import React from 'react';
import { motion } from 'framer-motion';
import { 
  Building2, 
  Users, 
  BarChart3, 
  Shield, 
  CreditCard, 
  Globe,
  FileText,
  Lock,
  Bell,
  Download
} from 'lucide-react';

const Features: React.FC = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const features = [
    {
      icon: Building2,
      title: "Gestion Multi-Établissements",
      description: "Créez et gérez plusieurs établissements depuis un seul tableau de bord. Attribuez des secrétaires à chaque établissement avec des permissions spécifiques.",
      color: "blue",
      points: [
        "Création illimitée d'établissements",
        "Tableaux de bord individuels",
        "Paramètres personnalisés par établissement"
      ]
    },
    {
      icon: Users,
      title: "Rôles et Permissions",
      description: "Superviseurs et secrétaires avec des permissions granulaires. Contrôlez précisément qui peut faire quoi dans votre organisation.",
      color: "green",
      points: [
        "Rôles pré-définis (superviseur/secrétaire)",
        "Permissions personnalisables",
        "Audit des actions utilisateurs"
      ]
    },
    {
      icon: BarChart3,
      title: "Suivi Financier Complet",
      description: "Suivez toutes les entrées et sorties d'argent avec preuves à l'appui. Générez des rapports détaillés et des analyses en temps réel.",
      color: "purple",
      points: [
        "Enregistrement des transactions",
        "Preuves numériques (factures, reçus)",
        "Rapports automatiques"
      ]
    },
    {
      icon: CreditCard,
      title: "Paiements en Ligne",
      description: "Acceptez les paiements en ligne pour vos établissements. Gérez les retraits directement depuis la plateforme.",
      color: "orange",
      points: [
        "Intégration Stripe/MPesa",
        "Paiements sécurisés",
        "Gestion des retraits"
      ]
    },
    {
      icon: Shield,
      title: "Sécurité Renforcée",
      description: "Vos données sont chiffrées de bout en bout. Authentification à deux facteurs et conformité RGPD garanties.",
      color: "red",
      points: [
        "Chiffrement AES-256",
        "2FA obligatoire",
        "Sauvegardes automatiques"
      ]
    },
    {
      icon: Globe,
      title: "Accessible Partout",
      description: "Accédez à EXELIUS depuis n'importe quel appareil. Interface responsive optimisée pour mobile et desktop.",
      color: "indigo",
      points: [
        "Application web responsive",
        "Hors ligne disponible",
        "Synchronisation en temps réel"
      ]
    }
  ];

  const workflow = [
    {
      step: 1,
      title: "Inscription du Superviseur",
      description: "Le superviseur crée son compte et choisit son abonnement",
      icon: Users
    },
    {
      step: 2,
      title: "Création des Établissements",
      description: "Ajoutez vos établissements et configurez leurs paramètres",
      icon: Building2
    },
    {
      step: 3,
      title: "Invitation des Secrétaires",
      description: "Créez des comptes secrétaires et attribuez-les aux établissements",
      icon: FileText
    },
    {
      step: 4,
      title: "Gestion Financière",
      description: "Les secrétaires enregistrent les transactions avec preuves",
      icon: BarChart3
    },
    {
      step: 5,
      title: "Suivi et Rapports",
      description: "Le superviseur visualise les états et génère des rapports",
      icon: Download
    }
  ];

  return (
    <section id="features" className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <motion.div 
          {...fadeInUp}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Fonctionnalités complètes pour votre succès
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Tout ce dont vous avez besoin pour gérer efficacement vos établissements et leurs finances
          </p>
        </motion.div>

        {/* Main Features Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-20">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              whileHover={{ y: -5 }}
              className="group"
            >
              <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 h-full">
                <div className={`inline-flex items-center justify-center w-14 h-14 rounded-xl bg-${feature.color}-100 text-${feature.color}-600 mb-6 group-hover:scale-110 transition-transform`}>
                  <feature.icon className="h-7 w-7" />
                </div>
                
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  {feature.title}
                </h3>
                
                <p className="text-gray-600 mb-4">
                  {feature.description}
                </p>
                
                <ul className="space-y-2">
                  {feature.points.map((point, i) => (
                    <li key={i} className="flex items-center text-sm text-gray-500">
                      <div className={`w-1.5 h-1.5 rounded-full bg-${feature.color}-500 mr-3`} />
                      {point}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Workflow Section */}
        <motion.div 
          {...fadeInUp}
          className="mb-16"
        >
          <h3 className="text-2xl font-bold text-center text-gray-900 mb-10">
            Comment EXELIUS fonctionne en 5 étapes
          </h3>
          
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-0.5 bg-gradient-to-b from-blue-500 to-purple-500 hidden lg:block" />
            
            <div className="space-y-12 lg:space-y-0">
              {workflow.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.2 }}
                  className={`flex flex-col lg:flex-row items-center ${
                    index % 2 === 0 ? 'lg:flex-row-reverse' : ''
                  }`}
                >
                  {/* Step content */}
                  <div className={`lg:w-1/2 ${index % 2 === 0 ? 'lg:pr-12' : 'lg:pl-12'}`}>
                    <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-100">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="flex items-center justify-center w-10 h-10 rounded-full bg-blue-100 text-blue-600 font-bold">
                          {item.step}
                        </div>
                        <item.icon className="h-5 w-5 text-gray-600" />
                      </div>
                      <h4 className="text-lg font-semibold text-gray-900 mb-2">
                        {item.title}
                      </h4>
                      <p className="text-gray-600">
                        {item.description}
                      </p>
                    </div>
                  </div>
                  
                  {/* Step indicator (mobile) */}
                  <div className="my-6 lg:hidden">
                    <div className="flex items-center justify-center w-12 h-12 rounded-full bg-blue-600 text-white font-bold">
                      {item.step}
                    </div>
                  </div>
                  
                  {/* Empty div for spacing */}
                  <div className="lg:w-1/2" />
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Security Features */}
        <motion.div 
          {...fadeInUp}
          className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-8 md:p-12"
        >
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <Lock className="h-12 w-12 text-blue-600 mx-auto mb-4" />
              <h4 className="font-semibold text-gray-900 mb-2">Données Chiffrées</h4>
              <p className="text-gray-600 text-sm">
                Toutes vos données sont chiffrées avec AES-256
              </p>
            </div>
            
            <div className="text-center">
              <Shield className="h-12 w-12 text-green-600 mx-auto mb-4" />
              <h4 className="font-semibold text-gray-900 mb-2">Conformité RGPD</h4>
              <p className="text-gray-600 text-sm">
                Respect total des réglementations de protection des données
              </p>
            </div>
            
            <div className="text-center">
              <Bell className="h-12 w-12 text-purple-600 mx-auto mb-4" />
              <h4 className="font-semibold text-gray-900 mb-2">Notifications en Temps Réel</h4>
              <p className="text-gray-600 text-sm">
                Soyez alerté de toutes les activités importantes
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Features;